import { Dna } from 'lucide-react'

import type { PublicPatient } from '@snootsvet/shared/portal'

import {
  EditActions,
  EditableField,
  EditableValue,
  EditButton,
  useEditableField,
} from '@/customer/components/editable-field'
import { BreedAutocomplete } from '@/customer/components/pet/breed-autocomplete'
import { useUpdatePet } from '@/customer/hooks/use-update-pet'

/**
 * Breed row. Delegates the input to `<BreedAutocomplete>`, so
 * suggestions come from the shared breed lists (species-filtered)
 * while free typing still saves. Empty input clears the value.
 */
export function BreedPetField({ pet }: { pet: PublicPatient }) {
  const updatePet = useUpdatePet(pet.id)
  const breedField = useEditableField({
    initialValue: pet.breed ?? '',
    onSave: async ({ value, setError }) => {
      const next = value.trim() || null
      if (next === pet.breed) return
      try {
        await updatePet.mutateAsync({ breed: next })
      } catch {
        setError('Something went wrong saving. Please try again.')
      }
    },
  })

  return (
    <EditableField
      label="Breed"
      icon={Dna}
      error={breedField.error}
      action={<EditButton field={breedField} label="breed" />}
    >
      {breedField.isEditing ? (
        <BreedAutocomplete
          species={pet.species}
          value={breedField.draft}
          onValueChange={breedField.setDraft}
          variant="underline"
          autoFocus
        />
      ) : (
        <EditableValue>
          {pet.breed ?? <span className="text-muted-foreground">Not set</span>}
        </EditableValue>
      )}
      <EditActions field={breedField} />
    </EditableField>
  )
}
